import {
  Component,
  DestroyRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Observable, take, tap } from 'rxjs';
import {
  selectAuthUserAlertsLoaded,
  selectAuthUserAlertsLoading,
  selectAuthUserTriggeredAlerts,
} from '../../reducers';

import { Alert } from '../../models/alert.models';
import { AppFeatures } from '../../app.features';
import { AppState } from 'src/app/reducers';
import { UserFacadeService } from '@services/user-facade.service';
import { ObjectEditorService } from '../../services/object-editor.service';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { deleteAlert } from '../../actions/feed.actions';
import { loadTriggeredAlerts } from '../../actions/auth.actions';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DialogCloseOptions, DialogCloseReason } from '@sui/dialog';
import { DialogService } from '@sui/dialog';
import { ConfirmDialogComponent } from '../../components/popups/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-alerts-center',
  templateUrl: './alerts-center.component.html',
  styleUrl: './alerts-center.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AlertsCenterComponent {
  alertsLoading$: Observable<boolean>;

  allAlerts: Alert[];
  alerts: Alert[];
  searchQuery: string;

  readonly AppFeatures = AppFeatures;

  private destroyRef = inject(DestroyRef);
  private dialogService = inject(DialogService);

  constructor(
    private store: Store<AppState>,
    public userFacade: UserFacadeService,
    private editService: ObjectEditorService,
    private translateService: TranslateService,
  ) {
    this.store
      .select(selectAuthUserAlertsLoaded)
      .pipe(
        take(1),
        tap((loaded) => {
          if (!loaded) {
            this.store.dispatch(loadTriggeredAlerts());
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectAuthUserTriggeredAlerts)
      .pipe(
        tap((alerts) => {
          this.allAlerts = alerts;
          this.handleSearchStringAlertChange(this.searchQuery); // Reapply search on updated alerts so mode (Paused/Running) stays in sync
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.alertsLoading$ = this.store.select(selectAuthUserAlertsLoading);
  }

  handleSearchStringAlertChange(query: string) {
    this.searchQuery = query;
    if (query) {
      this.alerts = this.allAlerts.filter(
        (alert) =>
          alert.title.toLowerCase().includes(query.toLowerCase()) ||
          alert.feed_title.toLowerCase().includes(query.toLowerCase()),
      );
    } else {
      this.alerts = this.allAlerts;
    }
  }

  editAlert(alert) {
    this.editService.editAlert(alert?.id);
  }

  deleteAlert(alert) {
    const title = this.translateService.instant(
      'feed-alerts.alert-delete-confirmation-title',
    );

    const message = this.translateService.instant(
      'feed-alerts.alert-delete-confirmation',
    );

    const dialogRef = this.dialogService.openSimpleDialog(
      ConfirmDialogComponent,
      {
        data: {
          title,
          message,
        },
        closeOnClick: false,
      },
    );

    dialogRef.closed.subscribe(
      (event: DialogCloseOptions<{ reason: DialogCloseReason }>) => {
        if (event.reason === DialogCloseReason.Confirm) {
          this.store.dispatch(deleteAlert({ alertId: alert.id }));
        } else if (event.reason === DialogCloseReason.Cancel) {
          console.log(`Action cancelled: ${event.reason}`);
        }
      },
    );
  }

  trackByAlertId(index: number, alert: Alert): string {
    return alert.id;
  }
}
